import React, { useEffect, useState } from 'react';

import { IconButton } from '@atlaskit/button/new';
import VidFullScreenOff from '@atlaskit/icon/core/minimize';
import { fg } from '@atlaskit/platform-feature-flags';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';
import {
	AnalyticsEventActions,
	AnalyticsEventSource,
	useAnalytics,
	useTeamCentralStore,
} from '@atlassian/ptc-common';

import { useReportingLinesContext } from '../ReportingLinesContext';

import { ReportingLinesEmptyState } from './EmptyState';
import { type ReportingLinesResponse } from './hooks';
import { DirectReportsSection } from './Sections/DirectReportsSection';
import { ManagerSection } from './Sections/ManagerSection';
import { PeersSection } from './Sections/PeersSection';
import { ReportingLinesSkeleton } from './Skeleton';
import * as Styled from './styles';
import { type ReportingLines } from './types';

export const REPORTING_LINES_USER_CARD_SLOTS_LIMIT = 8;

interface ReportingLinesChartProps {
	reportingLines: ReportingLines;
}

export const ReportingLinesChart = ({
	reportingLines,
}: ReportingLinesChartProps): React.JSX.Element => {
	const analytics = useAnalytics();
	const { fireEvent } = useAnalyticsEvents();
	const {
		managers: orderedManagerList,
		user: selectedUser,
		reports: directReports,
		peers,
	} = reportingLines;

	const [showAvatarStack, setShowAvatarStack] = useState(true);

	const [teamCentralStore] = useTeamCentralStore();
	const { getActiveWorkspaceId } = teamCentralStore.teamCentralHandlers;
	const { product = '' } = useReportingLinesContext();
	const workspaceUuid = getActiveWorkspaceId ? getActiveWorkspaceId() : null;

	const toggleAvatarStack = () => {
		if (fg('ptc-enable-ptc-common-analytics-refactor')) {
			fireEvent('ui.ReportingLinesChart.expanded', {
				product,
				workspaceUuid,
			});

			// used to count towards Atlas MAU
			fireEvent('ui.ui.viewed', {
				product,
				workspaceUuid,
			});
		} else {
			analytics.pushUIEvent({
				source: AnalyticsEventSource.USER_PROFILE_SCREEN,
				actionSubject: 'ReportingLinesChart',
				action: AnalyticsEventActions.EXPANDED,
				tags: ['watermelon'],
				attributes: { product, workspaceUuid },
			});

			// used to count towards Atlas MAU
			analytics.pushUIEvent({
				source: AnalyticsEventSource.USER_PROFILE_SCREEN,
				actionSubject: 'ui',
				action: AnalyticsEventActions.VIEWED,
				tags: ['watermelon'],
				attributes: { product, workspaceUuid },
			});
		}

		setShowAvatarStack((prev) => !prev);
	};

	useEffect(() => {
		if (fg('ptc-enable-ptc-common-analytics-refactor')) {
			fireEvent('ui.ReportingLinesChart.viewed', {
				product,
				workspaceUuid,
			});
		} else {
			analytics.pushUIEvent({
				source: AnalyticsEventSource.USER_PROFILE_SCREEN,
				actionSubject: 'ReportingLinesChart',
				action: AnalyticsEventActions.VIEWED,
				tags: ['watermelon'],
				attributes: { product, workspaceUuid },
			});
		}
	}, [analytics, product, workspaceUuid, fireEvent]);

	// Extracting these into constants breaks typechecking
	// Checks if any response data is missing (undefined/null) or if no IdP data exists (empty arrays)
	if (
		!selectedUser ||
		!orderedManagerList ||
		!directReports ||
		!peers ||
		(orderedManagerList.length === 0 && directReports.length === 0 && peers.length === 0)
	) {
		return <ReportingLinesEmptyState />;
	}

	const isManager = selectedUser.numberOfDirectReports !== 0;

	return (
		<>
			{!showAvatarStack && (
				<Styled.CollapseButton>
					<IconButton
						icon={VidFullScreenOff}
						label="Collapse"
						onClick={() => {
							if (fg('ptc-enable-ptc-common-analytics-refactor')) {
								fireEvent('ui.ReportingLinesChart.collapsed', {
									product,
									workspaceUuid,
								});
								// used to count towards Atlas MAU
								fireEvent('ui.ui.viewed', {
									product,
									workspaceUuid,
								});
							} else {
								analytics.pushUIEvent({
									source: AnalyticsEventSource.USER_PROFILE_SCREEN,
									actionSubject: 'ReportingLinesChart',
									action: AnalyticsEventActions.COLLAPSED,
									tags: ['watermelon'],
									attributes: { product, workspaceUuid },
								});

								// used to count towards Atlas MAU
								analytics.pushUIEvent({
									source: AnalyticsEventSource.USER_PROFILE_SCREEN,
									actionSubject: 'ui',
									action: AnalyticsEventActions.VIEWED,
									tags: ['watermelon'],
									attributes: { product, workspaceUuid },
								});
							}

							setShowAvatarStack(true);
						}}
						testId="reporting-lines-collapse-button"
					/>
				</Styled.CollapseButton>
			)}
			<ManagerSection
				orderedManagerList={orderedManagerList}
				showAvatarStack={showAvatarStack}
				toggleAvatarStack={toggleAvatarStack}
				isSelectedUserManager={isManager}
			/>
			{isManager ? (
				<DirectReportsSection
					selectedUser={selectedUser}
					directReports={directReports}
					showAvatarStack={showAvatarStack}
					toggleAvatarStack={toggleAvatarStack}
				/>
			) : (
				<PeersSection
					selectedUser={selectedUser}
					peers={peers}
					showAvatarStack={showAvatarStack}
					toggleAvatarStack={toggleAvatarStack}
				/>
			)}
		</>
	);
};

interface ReportingLinesChartFromResponseProps {
	reportingLinesResponse: ReportingLinesResponse;
	isResponseEmpty: boolean;
}
export const ReportingLinesChartFromResponse = ({
	reportingLinesResponse: { isLoading, reportingLines },
	isResponseEmpty,
}: ReportingLinesChartFromResponseProps): React.JSX.Element => {
	if (isLoading) {
		return <ReportingLinesSkeleton />;
	}

	if (!isResponseEmpty && !!reportingLines) {
		return <ReportingLinesChart reportingLines={reportingLines} />;
	}
	return <ReportingLinesEmptyState />;
};
